import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { DateHelperService } from './date-helper.service';
import { Observable, Subject, EMPTY } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreativeBlueprint, ProjectSection } from '../interfaces';
import { GeneralUtilsService } from './general-utils.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectSectionService {
  constructor(
    private readonly api: ApiService,
    private readonly translateService: TranslatePipe,
    private readonly dateHelperService: DateHelperService,
    private readonly generalUtilsService: GeneralUtilsService
  ) {}

  public getSections(projectId: string): Observable<any> {
    return this.api.httpGet('/api/projects/' + projectId + '/sections').pipe(
      map((data: any) => {
        if (data && Array.isArray(data)) {
          data.forEach((s: any) => {
            s.startDate = s.startDate ? this.dateHelperService.fromTimestampToDate(s.startDate) : null;
            s.endDate = s.endDate ? this.dateHelperService.fromTimestampToDate(s.endDate) : null;
          });
        }
        return data;
      })
    );
  }

  public getAllSubsections(): Observable<any> {
    return this.api.httpGet('/api/commons/subsections/all');
  }

  public getCreativeBlueprint(projectId: string, companyId: string): Observable<CreativeBlueprint> {
    return this.api.httpGet(
      `/api/creative-blueprints/by-element?elementType=PROJECT&elementId=${projectId}&companyId=${companyId}`
    );
  }

  public getProjectSectionList(projectId: string): Observable<ProjectSection[]> {
    return this.api
      .httpGet('/api/projects/' + projectId + '/sections/subsections')
      .pipe(map((response: any) => response as ProjectSection[]));
  }

  public getSectionsAndSubsections(projectId: string): Observable<any> {
    const subject: Subject<any> = new Subject<any>();

    if (!projectId) {
      return EMPTY;
    }
    this.api.httpGet('/api/projects/' + projectId + '/sections/subsections').subscribe({
      next: (data: any) => {
        if (data && Array.isArray(data)) {
          data.forEach((section: any) => {
            //section.name = this.getSectionName(section.section);
            section.subsections = section.projectSubSections;
            if (section.subsections && Array.isArray(section.subsections)) {
              section.subsections.map((sub: any) => {
                sub.url = section.id + '/' + sub.id;
              });
            }
          });
        }

        subject.next(data);
        subject.complete();
      },
      error: (err: Error) => {
        subject.error(err);
        subject.complete();
      },
    });

    return subject.asObservable();
  }

  public createSection(projectId: string, section: any): Observable<any> {
    return this.api.httpPost('/api/projects/' + projectId + '/sections', section);
  }

  public createSubsection(projectId: string, sectionId: string, subsection: any): Observable<any> {
    return new Observable((observer: any) => {
      if (!this.generalUtilsService.isUUID(subsection.id)) delete subsection.id;

      this.api
        .httpPost('/api/projects/' + projectId + '/sections/' + sectionId + '/subsections', subsection)
        .subscribe({
          next: (data: any) => {
            if (data.startDate) data.startDate = this.dateHelperService.fromTimestampToDate(data.startDate);
            if (data.endDate) data.endDate = this.dateHelperService.fromTimestampToDate(data.endDate);

            observer.next(data);
            observer.complete();
          },
          error: (err: Error) => {
            observer.error(err);
          },
        });
    });
  }

  public getSubsections(projectId: string, sectionId: string): Observable<any> {
    const subject: Subject<any> = new Subject<any>();
    if (!projectId) {
      return EMPTY;
    }
    this.api.httpGet('/api/projects/' + projectId + '/sections/' + sectionId + '/subsections').subscribe({
      next: (data: any) => {
        const subsections: any[] = [];
        data.forEach((subsection: any) => {
          if (subsection.startDate)
            subsection.startDate = this.dateHelperService.fromTimestampToDate(subsection.startDate);
          if (subsection.endDate) subsection.endDate = this.dateHelperService.fromTimestampToDate(subsection.endDate);
          subsections.push(subsection);
        });
        subject.next(subsections);
      },
      error: (err: any) => {
        subject.error(err);
      },
    });

    return subject.asObservable();
  }

  public deleteSubsection(projectId: string, sectionId: string, subsectionId: string): Observable<any> {
    return this.api.httpDelete(
      '/api/projects/' + projectId + '/sections/' + sectionId + '/subsections/' + subsectionId
    );
  }

  public updateOrderNumber(o: any, projectId: string, sectionId: string): Observable<any> {
    return this.api.httpPost(`/api/projects/${projectId}/sections/${sectionId}/subsections/order`, o);
  }

  public updateSubsectionsOrder(projectId: string, sectionId: string, subsections: any[]): Observable<any> {
    return this.api.httpPost(`/api/projects/${projectId}/sections/${sectionId}/subsections/order/batch`, subsections);
  }

  public getSectionName(name: string): string {
    let sectionName: string = name;

    switch (name) {
      case 'CREATIVE_PARTNER_SELECTION':
        sectionName = 'Creative Partner Selection';
        break;
      case 'CREATIVE_BRIEF':
        sectionName = 'Creative Brief';
        break;
      case 'CREATIVE_WORK':
        sectionName = 'Creative Work';
        break;
      case 'PRODUCTION_PARTNER_SELECTION':
        sectionName = this.translateService.transform('PRODUCTION PARTNER SELECTION');
        break;
      case 'PRODUCTION_BRIEF':
        sectionName = this.translateService.transform('PRODUCTION BRIEF');
        break;
      case 'PRODUCTION_COMPANY_PROPOSALS':
        sectionName = this.translateService.transform('PRODUCTION COMPANY PROPOSALS');
        break;
      case 'PRE_PRODUCTION':
        sectionName = 'Pre-Production';
        break;
      case 'SHOOTING':
        sectionName = 'Shooting';
        break;
      case 'POST_PRODUCTION':
        sectionName = 'Post-Production';
        break;
      case 'SELF_FIX_EDIT':
        sectionName = 'Self-fix Edit';
        break;
      case 'DIFFUSION':
        sectionName = 'Diffusion';
        break;
      case 'BRIEF':
        sectionName = 'Ideation';
        break;
    }

    return sectionName;
  }
}
